import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { Container, Typography, Grid, Button } from "@material-ui/core";
import PropTypes from "prop-types";
import Page from "../../../components/Page";
import FormInput from "./FormInput";
import CreateButton from "./Button";
import axios from "../../../axios";
import Snackbar from "../../../components/Snackbar";
import validate from "validate.js";
import { Link as RouterLink } from "react-router-dom";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(3),
	},
	inputContent: {
		marginTop: theme.spacing(1.5),
	},
	header: {
		marginTop: theme.spacing(3),
	},
}));

let schema = {
	name: {
		presence: { allowEmpty: false, message: "is required" },
	},
	email: {
		presence: { allowEmpty: false, message: "is required" },
		email: true,
	},
	permissions: { presence: { allowEmpty: false, message: "is required" } },
	active: {
		presence: {
			presence: { allowEmpty: false },
		},
	},
};

const initialState = {
	isValid: false,
	values: {
		active: false,
		permissions: ["CLIENT"],
	},
	touched: {
		active: true,
		permissions: true
	},
	errors: {},
};

const User = ({ match }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const { userid } = match.params;
	const [isUpdate, setIsUpdate] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("");
	const [formState, setFormState] = useState(initialState);
	const history = useHistory();

	useEffect(() => {
		// if id isset then we want to fetch the user from the api and fill the text fields
		if (userid) {
			delete schema.password;
			delete schema.confirmpassword;

			setIsUpdate(true);
			getUser();
		}
	}, [userid]);

	const getUser = () => {
		axios
		.get(`/api/user/users/${userid}`)
		.then((response) => {
			console.log(response);

			const user = response.data.user;

			setFormState((prevState) => ({
				...prevState,
				values: {
					id: user.id,
					initials: user.initials,
					name: user.name,
					email: user.email,
					phone: user.phone,
					company: user.company,
					permissions: user.permission.split(","),
					active: user.active,
				},
				touched: {
					...prevState.touched,
					id: true,
					initials: true,
					name: true,
					email: true,
					phone: true,
					company: true,
					permissions: true,
					active: true,
				},
			}));
		})
		.catch((error) => {
			console.log(error);
		});
	}

	const handleChange = (event) => {
		event.persist();

		setFormState((prevFormState) => ({
			...prevFormState,
			values: {
				...prevFormState.values,
				[event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
			},
			touched: {
				...prevFormState.touched,
				[event.target.name]: true,
			},
		}));
	};

	const handleCheckboxChange = (event) => {
		event.persist();

		const {
			target: { value },
		} = event;

		const permissions = typeof value === "string" ? value.split(",") : value;

		setFormState((prevFormState) => ({
			...prevFormState,
			values: {
				...prevFormState.values,
				[event.target.name]: permissions,
			},
			touched: {
				...prevFormState.touched,
				[event.target.name]: true,
			},
		}));
	};

	// Check if input has error
	const hasError = (field) => !!(formState.touched[field] && formState.errors[field]);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState((prevFormState) => ({
			...prevFormState,
			isValid: !errors,
			errors: errors || {},
		}));
	}, [formState.values, setFormState]);

	const handleSnackbarClick = (message, severity) => {
		setSnackbarMessage(message);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	const handleSnackbarClose = (event, reason) => {
		if (reason === "clickaway") return;
		setSnackbarMessage("");
		setSnackbarSeverity("");
		setSnackbarOpen(false);
	};

	const onCreate = async () => {
		let body = formState.values;
		setLoading(true);

		const stringPermission = formState.values.permissions.join(",");
		body.permission = stringPermission;
		delete body.permissions;

		console.log(body);

		try {
			if (isUpdate) {
				const updateUser = await axios.put("/api/user/update/" + userid, body);

				if (updateUser.status === 200) {
					handleSnackbarClick("Bruger er blevet opdateret", "success");
					getUser();
				}
			} else {
				const newUser = await axios.post("/api/user/registerrstpwd", body);

				console.log(newUser);
				handleSnackbarClick("Bruger er nu oprettet", "success");
				setFormState({
					...initialState,
					values: {
						...initialState.values,
					},
				});
			}
		} catch (error) {
			console.log(error, error.response);
			if (error.response) {
				if (error.response.status === 422) {
					const validationErrors = error.response.data.errors.map((error) => {
						var errors = "";
						for (var key in error) {
							errors += error[key] + "\n";
						}
						return errors;
					});
					handleSnackbarClick(validationErrors, "error");
				} else if (error.response.status === 409) {
					if (error.response.status) handleSnackbarClick("User already exist", "error");
				} else if (error.response.status === 400 && error.response.data.errors) {
					if (error.response.data.errors[0].message) {
						const validationErrors = error.response.data.errors.map((error) => {
							return error.message;
						});
						handleSnackbarClick(validationErrors, "error");
					} else {
						handleSnackbarClick(error.response.data.errors, "error");
					}
				} else {
					console.log("There was an error creating/updating the user");
					handleSnackbarClick("Der gik noget galt", "error");
				}
			} else {
				console.log("There was an error creating/updating the user");
				handleSnackbarClick("Der gik noget galt", "error");
			}
		}
		setLoading(false);
	};

	const deleteUser = async () => {
		const url =
			"/api/user/delete/" + userid;

		try {
			await axios.delete(url);

			history.push("/administration/elektrolysebrugere");
		} catch (error) {
			console.log(error);
			handleSnackbarClick("Kunne ikke slette brugere. Prøv igen eller kontakt venligst IT!", "error");
		}
	};

	const sendResetPasswordLink = async () => {
		try {
			
			const response = await axios.post("/api/user/forgotpassword/", { email: formState.values.email });

			if (response.status === 200) {
				handleSnackbarClick("Mail er sendt til kunden med link til at nulstille deres kodeord. ", "success");
			}
		} catch (error) {
			console.log(error);
			handleSnackbarClick("Kunne ikke sende mail til kunden. Prøv igen ellers kontakt IT", "error");
		}
	}

	return (
		<Page className={classes.root}>
			<Container maxWidth="lg">
				<Grid className={classes.header} alignItems="flex-end" container justify="space-between" spacing={3}>
					<Grid item>
						<Typography component="h2" variant="overline">
							Bruger
						</Typography>
						<Typography component="h1" variant="h3">
							{isUpdate ? "Opdater bruger" : "Opret ny bruger"}
						</Typography>
					</Grid>
					{isUpdate ? (
						<Grid item>
							<Grid container spacing={3}>
								<Grid item>
									<Button
										color="primary"
										className={classes.button}
										variant="contained"
										onClick={() => {
											if (window.confirm("Er du sikker på at du vil sende mail til kunden om nulstilling af kodeord?")) {
												sendResetPasswordLink();
											}
										}}
										
									>
										Send nulstil kodeord link
									</Button>
								</Grid>
								<Grid item>
									<Button
										color="primary"
										className={classes.button}
										variant="contained"
										component={RouterLink}
										to={`/administration/elektrolysebrugere/${userid}/adgange`}
									>
										Tildel Elektrolyse styringer
									</Button>
								</Grid>
								<Grid item>
									<Button
										color="secondary"
										className={classes.button}
										variant="contained"
										onClick={() => {
											if (window.confirm("Er du sikker på at du vil slette denne bruger?")) {
												deleteUser();
											}
										}}
									>
										Slet
									</Button>
								</Grid>
							</Grid>
						</Grid>
					): null}
				</Grid>
				<FormInput
					isUpdate={isUpdate}
					hasError={hasError}
					formState={formState}
					handleChange={handleChange}
					handleCheckboxChange={handleCheckboxChange}
					className={classes.inputContent}
				/>
				<CreateButton isUpdate={isUpdate} formDisabled={!formState.isValid} loading={loading} onClick={onCreate} className={classes.button} />
			</Container>
			{snackbarMessage && snackbarOpen && snackbarSeverity ? (
				<Snackbar open={snackbarOpen} autoHideDuration={6000} message={snackbarMessage} onClose={handleSnackbarClose} severity={snackbarSeverity} />
			) : null}
		</Page>
	);
};

User.propTypes = {
	match: PropTypes.object.isRequired,
};

export default User;
